/**
 * ChatUnavailable - Shown when chat is outside the visibility window
 */

import { Lock, Calendar, ArrowLeft } from 'lucide-react'
import { differenceInDays, format } from 'date-fns'

interface ChatUnavailableProps {
  chatVisibleFrom: Date | null
  chatVisibleUntil: Date | null
  onBack: () => void
}

export default function ChatUnavailable({
  chatVisibleFrom,
  chatVisibleUntil,
  onBack
}: ChatUnavailableProps) {
  const now = new Date()
  const isBeforeWindow = chatVisibleFrom && now < chatVisibleFrom
  const isAfterWindow = chatVisibleUntil && now > chatVisibleUntil

  let message = ''
  let detail = ''

  if (isBeforeWindow) {
    const daysUntil = differenceInDays(chatVisibleFrom, now)
    message = 'Chat Not Yet Available'
    detail = `This chat will open ${daysUntil} day${daysUntil !== 1 ? 's' : ''} before your wedding (${format(chatVisibleFrom, 'MMM d, yyyy')})`
  } else if (isAfterWindow) {
    message = 'Chat Closed'
    detail = `This chat closed on ${format(chatVisibleUntil, 'MMM d, yyyy')} (30 days after your event)`
  } else {
    message = 'Chat Unavailable'
    detail = 'This chat is not currently available'
  }

  return (
    <div className="flex flex-col h-full">
      {/* Back button */}
      <div className="px-4 py-3 border-b border-gray-200">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Back to chats</span>
        </button>
      </div>

      {/* Unavailable message */}
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 text-gray-500">
        {isBeforeWindow ? (
          <Lock className="w-16 h-16 mb-4 text-gray-300" />
        ) : (
          <Calendar className="w-16 h-16 mb-4 text-gray-300" />
        )}

        <h3 className="font-semibold text-gray-700 text-lg mb-2">
          {message}
        </h3>

        <p className="text-sm max-w-xs">
          {detail}
        </p>

        {isBeforeWindow && (
          <div className="mt-4 text-xs text-gray-400">
            <p>Chat is available:</p>
            <p className="font-medium text-gray-600 mt-1">
              30 days before → 30 days after your event
            </p>
          </div>
        )}

        {isAfterWindow && (
          <div className="mt-4 text-xs bg-blue-50 text-blue-700 px-4 py-2 rounded-lg">
            <p>For support or questions, please contact us directly</p>
          </div>
        )}
      </div>
    </div>
  )
}
